import { uniqBy } from 'lodash';
import moment from 'moment';

class PaymentController {

  constructor($scope, $window, $timeout, basket, order, checkout, configService, messageService, markdownService, storeService, guestnameService, checkoutValidationService, guestApi, navigationService) {
    this.$scope = $scope;
    this.$window = $window;
    this.$timeout = $timeout;
    this.basket = basket;
    this.order = order;
    this.checkout = checkout;
    this.config = configService;
    this.messageService = messageService;
    this.moment = moment;
    this.markdownService = markdownService;
    this.storeService = storeService;
    this.guestName = guestnameService;
    this.checkoutValidation = checkoutValidationService;
    this.guestApi = guestApi;
    this.navigationService = navigationService;
  }

  $onInit () {
    this.loading = true;
    this.form = {};
    this.showDiscountInputs = false;

    this.gateway = this.config.get('gateways');

    this.watchForErrors();

    return this.basket.promise.then(() => {
      if (!this.basket.empty()) {
        return this.order.generateOrder(this.basket.contents.identity_code)
          .then(() => {
            if (this.order.contents.vouchers.length > 0
              && this.order.contents.vouchers[0]
              && this.order.contents.vouchers[0].code) {
              this.codetype = 'Voucher';
              this.showDiscountInputs = true;
            } else if (this.order.contents.promo_details) {
              this.codetype = 'Promotional Code';
              this.showDiscountInputs = true;
            }

            if (this.order.contents.customer) {
              if (this.order.contents.customer.marketing === 1) {
                this.order.contents.customer.marketing = true;
              } else {
                this.order.contents.customer.marketing = false;
              }
            }

            this.checkout.setTokenCountdown();
            this.setIntegrationGuestDetails();
            this.generatePaymentRequestConfig();

            return true;
          })
          .catch(() => {
            this.hasError = true;

            return false;
          })
          .then(response => {
            this.checkout.setStepByName('payment');
            this.loading = false;

            return response;
          });
      }
    });
  }

  handlePaymentGatewayError (error, report = false) {
    this.checkout.error = error;
    this.checkout.scrollToError();
    this.basket.saving = false;
    this.checkout.saving = false;
    if (report && this.$window.Sentry) {
      this.$window.Sentry.withScope(() => {
        this.$window.Sentry.captureMessage(`${report}: ${this.order.contents.basket_identity_code}`);
      });
    }
  }

  watchForErrors () {
    this.$scope.$watch(() => this.checkout.error, (newVal) => {
      if (newVal) {
        this.loading = false;
        this.hasError = true;
      }

      if (!newVal) {
        this.hasError = false;
      }
    });
  }

  voucherType () {
    switch (this.codetype) {
    case 'Voucher':
      this.order.contents.promo_details = null;
      if (this.order.contents.vouchers.length < 1) {
        this.order.contents.vouchers = [ {} ];
      }
      break;

    case '':
    default:
      this.order.contents.vouchers = [];
      break;
    }
  }

  getSetting (field) {
    if (this.content && this.content.settings && this.content.settings[field]) {
      return this.content.settings[field];
    }
  }

  orderGenerated () {
    if (!this.loading && this.order.contents.basket_identity_code) {
      return true;
    }

    return false;
  }

  paysafeTimestampInvalid () {
    return this.gateway && this.gateway.paysafe && !this.checkout.checkTimestamp();
  }

  displayPaymentGatewayName () {
    if (this.gateway) {
      if (this.gateway.stripe) {
        return 'Stripe';
      }

      if (this.gateway.paysafe) {
        return 'Paysafe';
      }
    }

    return null;
  }

  displayPaymentFields () {
    return !this.checkout.checkTimestamp()
      && !!this.displayPaymentGatewayName()
      && this.order.contents.total > 0;
  }

  setIntegrationGuestDetails () {
    this.integrationGuestDetails = [];
    const loggedInId = this.basket.contents.client_id;

    return this.guestApi.getLeadGuest(this.basket.contents.identity_code)
      .then(leadGuest => {
        if (leadGuest) {
          this.createPaymentGuest(leadGuest, loggedInId);
        }
      })
      .catch(() => {
        //
      })
      .then(() => {
        this.basket.contents.guest_details.forEach(guest => {
          this.createPaymentGuest(guest, loggedInId);
        });

        this.integrationGuestDetails = uniqBy(this.integrationGuestDetails.map(guest => {
          if (guest.integrationReference) {
            guest.integrationReference = parseInt(guest.integrationReference);
          }

          return guest;
        }), 'integrationReference');
      });
  }

  createPaymentGuest (guest, loggedInId) {
    if (guest.integration_reference) {
      let paymentGuest = {
        id: guest.id,
        firstName: guest.first_name,
        lastName: guest.last_name,
        email: guest.email || '',
        phone: guest.phone || guest.homePhone || guest.mobilePhone || guest.workPhone || '',
        fullName: this.guestName.format(guest),
        integrationReference: guest.integration_reference
      };

      if (loggedInId && (guest.integration_reference === loggedInId)) {
        paymentGuest.loggedIn = true;
      }

      this.integrationGuestDetails.push(paymentGuest);
    }
  }

  showPromoFields () {
    return this.showDiscountInputs
      && ((this.codetype === 'Promotional Code'
      && this.getSetting('allow_promos'))
      || (!this.codetype
        && this.getSetting('allow_promos')
        && !this.displayVoucherInput()));
  }

  showVoucherFields () {
    return this.showDiscountInputs
      && ((this.codetype === 'Voucher'
      && this.displayVoucherInput())
      || (!this.codetype
        && this.displayVoucherInput()
        && !this.getSetting('allow_promos')));
  }

  showHideVoucher () {
    if (!this.showDiscountInputs) {
      this.order.contents.vouchers = [];
      this.order.contents.promo_details = '';
      this.codetype = '';
    }

    if (this.showPromoFields()) {
      this.order.contents.vouchers = [];
    }

    if (this.showVoucherFields()) {
      this.order.contents.vouchers = [ {} ];
    }
  }

  displayPaymentInformation () {
    return this.displayPaymentGatewayName()
      && this.orderGenerated()
      && this.order.contents.total > 0;
  }

  validatePromo () {
    if (this.order.contents.promo_details) {
      this.$timeout.cancel(this.promoTimeout);
      return new Promise(resolve => {
        this.promoTimeout = this.$timeout(() => {
          this.promoSaving = true;
          return this.checkoutValidation.validatePromo(this.order)
            .then(response => {
              this.discountFeedback = response;
            })
            .catch(() => {
              this.clearDiscount();
            })
            .then(() => {
              this.promoSaving = false;
              resolve();
            });
        }, 750);
      });
    }

    this.$timeout.cancel(this.promoTimeout);
    this.clearDiscount();
  }

  validateVoucher () {
    this.discountFeedback = null;

    if (this.order.contents.vouchers
      && this.order.contents.vouchers[0].code) {
      this.voucherSaving = true;
      return this.checkoutValidation.validateVoucher(this.order)
        .then(response => {
          this.discountFeedback = response;
        })
        .catch(() => {
          this.clearDiscount();
        })
        .then(() => {
          this.voucherSaving = false;
        });
    }

    this.clearDiscount();
  }

  clearDiscount () {
    this.discountFeedback = null;
    this.order.contents.total = this.order.contents.original_total;
  }

  hasDiscountFeedback () {
    return !!this.discountFeedback;
  }

  displayDiscountInputs() {
    return (this.displayVoucherInput()
      || this.getSetting('allow_promos'))
      && this.order.contents.original_total > 0;
  }

  displayDiscountSelect() {
    return this.displayVoucherInput()
      && this.getSetting('allow_promos')
      && this.order.contents.original_total > 0;
  }

  displayVoucherInput() {
    return !!(this.getSetting('oevoucher')
      || this.getSetting('allow_gifted_vouchers'));
  }

  alexanderPromo() {
    return !!(this.getSetting('oevoucher') && this.getSetting('allow_promos'));
  }

  displayPaymentRequestButton() {
    return !!this.gateway.stripe
      && this.displayPaymentFields();
  }

  generatePaymentRequestConfig() {
    this.paymentRequestConfig = {
      country: this.gateway.stripe.country.toUpperCase(),
      currency: this.order.contents.currency_code.toLowerCase(),
      amount: this.order.contents.total
    };
  }

  handlePaymentButtonSuccess(response) {
    this.order.contents.customer = response.customer;
    this.order.handleUpdateSuccess(response.token, response.timestamp, response.card);

    this.checkout.setTokenCountdown();

    return this.navigationService.next();
  }
}

PaymentController.$inject = [ '$scope', '$window', '$timeout', 'basket', 'order', 'checkout', 'configService', 'messageService', 'markdownService', 'storeService', 'guestnameService', 'checkoutValidationService', 'guestApi', 'navigationService' ];

export default PaymentController;



// WEBPACK FOOTER //
// ./frontend/storefront/app/modules/checkout/controllers/payment.controller.js